import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, Github, ArrowUpRight, ChevronDown } from 'lucide-react';
import { projects } from '../mock';

/* ── Derive categories with counts ── */
const buildCategories = (list) => {
  const counts = list.reduce((acc, p) => {
    acc[p.category] = (acc[p.category] || 0) + 1;
    return acc;
  }, {});
  return [
    { label: 'All', count: list.length },
    ...Object.entries(counts).map(([label, count]) => ({ label, count }))
  ];
};

const useReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const ProjectCard = ({ project, index }) => {
  const [cardRef, cardVisible] = useReveal(0.08);

  return (
    <div
      ref={cardRef}
      className="project-card"
      style={{
        opacity: cardVisible ? 1 : 0,
        transform: cardVisible ? 'translateY(0) scale(1)' : 'translateY(28px) scale(0.97)',
        transition: `opacity 0.65s ease ${(index % 2) * 0.12}s, transform 0.65s ease ${(index % 2) * 0.12}s`
      }}
    >
      {/* Image */}
      <div className="card-image-wrap" style={{ height: '300px' }}>
        <img
          src={project.image}
          alt={project.title}
          style={{ height: '300px', objectFit: 'cover' }}
          loading="lazy"
        />
        <div className="card-image-overlay">
          {project.liveUrl !== "#" && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
              className="btn-accent gap-1.5 px-3 py-2 text-xs"
              style={{ border: 'none', textDecoration: 'none', flexShrink: 0 }}
              onClick={e => e.stopPropagation()}>
              Live <ExternalLink size={12} />
            </a>
          )}
          {project.githubUrl !== "#" && (
            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer"
              className="btn-secondary gap-1.5 px-3 py-2 text-xs"
              style={{ textDecoration: 'none', flexShrink: 0 }}
              onClick={e => e.stopPropagation()}>
              <Github size={12} /> Code
            </a>
          )}
          {project.liveUrl === "#" && project.githubUrl === "#" && (
            <span className="mono" style={{
              background: 'rgba(9,9,11,0.8)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '6px', padding: '4px 10px', fontSize: '0.65rem',
              color: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)'
            }}>
              Private / Internal
            </span>
          )}
        </div>

        {/* Category badge */}
        <div className="absolute top-3 left-3" style={{ zIndex: 2 }}>
          <span className="mono" style={{
            background: 'rgba(9,9,11,0.75)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '6px', padding: '3px 9px',
            fontSize: '0.65rem', color: 'rgba(255,255,255,0.85)',
            fontWeight: 500, letterSpacing: '0.06em',
            backdropFilter: 'blur(8px)'
          }}>
            {project.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold mb-2"
          style={{ fontSize: '1.05rem', color: 'var(--text-primary)', lineHeight: 1.3 }}>
          {project.title}
        </h3>
        <p className="text-sm mb-4"
          style={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>
          {project.description}
        </p>



        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '14px' }}
          className="flex items-center justify-between">
          {project.liveUrl !== "#" ? (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-medium"
              style={{ color: 'var(--accent)', textDecoration: 'none', transition: 'gap 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.gap = '8px'; }}
              onMouseLeave={e => { e.currentTarget.style.gap = '6px'; }}>
              View Project <ArrowUpRight size={13} />
            </a>
          ) : (
            <span className="text-xs mono" style={{ color: 'var(--text-muted)' }}>
              Private Project
            </span>
          )}
          {project.githubUrl !== "#" && (
            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--text-muted)', transition: 'color 0.2s ease', display: 'flex' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              aria-label="GitHub">
              <Github size={15} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────── */
const INITIAL_SHOW = 6;
const LOAD_STEP = 4;

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW);
  const [headerRef, headerVisible] = useReveal(0.2);

  const categories = buildCategories(projects);

  const handleFilter = (cat) => {
    setActiveFilter(cat);
    setVisibleCount(INITIAL_SHOW);
  };

  const filtered = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  const shown = filtered.slice(0, visibleCount);
  const remaining = filtered.length - shown.length;

  const progressPct = (shown.length / filtered.length) * 100;

  return (
    <section id="work" className="portfolio-section-alt"
      style={{ paddingTop: '100px', paddingBottom: '100px' }}>
      <div className="max-w-6xl mx-auto px-6">

        {/* ── Header ── */}
        <div
          ref={headerRef}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            marginBottom: '52px'
          }}
        >
          <p className="section-label mb-3">Selected Work</p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
            <div>
              <h2 className="font-bold tracking-tight"
                style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', lineHeight: 1.1 }}>
                Projects I've{' '}
                <span className="accent-text">Shipped</span>
              </h2>
              <p className="mt-2 text-sm"
                style={{ color: 'var(--text-secondary)', maxWidth: '460px', lineHeight: 1.65 }}>
                A curated collection of products and tools I've designed and built end-to-end.
              </p>
            </div>

            {/* Category filters with counts */}
            <div className="flex flex-wrap gap-1.5">
              {categories.map(({ label, count }) => (
                <button
                  key={label}
                  onClick={() => handleFilter(label)}
                  className={`filter-tab ${activeFilter === label ? 'active' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  {label}
                  <span style={{
                    background: activeFilter === label ? 'var(--accent)' : 'var(--border-color)',
                    color: activeFilter === label ? '#fff' : 'var(--text-muted)',
                    borderRadius: '100px', fontSize: '0.6rem', fontWeight: 700,
                    padding: '1px 6px',
                    transition: 'background-color 0.22s ease, color 0.22s ease',
                    fontFamily: "'JetBrains Mono', monospace"
                  }}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Grid ── */}
        {filtered.length === 0 ? (
          <div className="py-20 text-center" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            No projects in this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {shown.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        )}

        {/* ── Load More ── */}
        {remaining > 0 && (
          <div className="flex flex-col items-center gap-3 mt-10">
            <p className="text-xs mono" style={{ color: 'var(--text-muted)' }}>
              Showing {shown.length} of {filtered.length} projects
            </p>
            <div style={{
              width: '180px', height: '2px',
              background: 'var(--border-color)', borderRadius: '2px', overflow: 'hidden'
            }}>
              <div style={{
                height: '100%', borderRadius: '2px',
                background: 'var(--gradient-text)',
                width: `${progressPct}%`,
                transition: 'width 0.45s ease'
              }} />
            </div>
            <button
              onClick={() => setVisibleCount(v => v + LOAD_STEP)}
              className="btn-ghost-accent flex items-center gap-2 px-6 py-2.5 text-sm mt-1"
              style={{ cursor: 'pointer' }}
            >
              Load {Math.min(remaining, LOAD_STEP)} More
              <ChevronDown size={15} />
            </button>
          </div>
        )}

        {/* ── All shown ── */}
        {remaining === 0 && filtered.length > INITIAL_SHOW && (
          <div className="flex flex-col items-center gap-2 mt-10">
            <div style={{ width: '180px', height: '2px', background: 'var(--gradient-text)', borderRadius: '2px' }} />
            <p className="text-xs mono" style={{ color: 'var(--text-muted)' }}>
              All {filtered.length} projects shown
            </p>
          </div>
        )}

      </div>
    </section>
  );
};

export default Portfolio;
